import { useCallback, useMemo, Fragment } from 'react';
import type { SurveyQuestionType } from '@trinity/chat/enums';
import type { SurveyAggregate, SurveyQuestion, ShortTextResponseQuestion, TextResponseQuestion, HybridSurveyResponseUserId } from '@trinity/chat/types/survey';
import { ControlledTablePagination } from '@trinity/chat-components/components/presentation/TablePagination.js';
import type { ResponseTextItemProps } from '@/components/presentation/ProjectSurveyResponses';
import { ResponseTextItem } from '@/components/presentation/ProjectSurveyResponses';
import { useSelectRespondentProps, useSelectStarredProjectSurveyAnswer, useInitialStarredResponses, useProjectSurveyResponsesState } from '@/containers/SurveyResponses/hooks';
import styles from './style/ResponseText.module.css';

type Props = {
  question: TextResponseQuestion.Question;
  rationale: SurveyAggregate.RationaleMap;
  responses: ShortTextResponseQuestion.Aggregate.Map;
};

type ResponseItem = {
  rationale?: string;
  value: string;
  userId: HybridSurveyResponseUserId;
};

type ResponseTextListProps = {
  items: ResponseItem[];
  renderItem: (props: ResponseTextItemProps) => React.ReactNode;
  question: SurveyQuestion;
  rationaleMap: SurveyAggregate.RationaleMap;
};

export const useSortStarrableResponses = (questionId: number) => {
  const starred = useInitialStarredResponses(questionId);

  const selectRespondentProps = useSelectRespondentProps();

  const sort = useCallback((items: ResponseItem[]) => {
    return items
      .sort((a, b) => {
        const responseIdA = selectRespondentProps(a.userId).responseId;
        const respondeIdB = selectRespondentProps(b.userId).responseId;
        const aStarred = starred.includes(responseIdA);
        const bStarred = starred.includes(respondeIdB);
        return (aStarred === bStarred) ? 0 : aStarred ? -1 : 1;
      });
  }, [
    selectRespondentProps,
    starred,
  ]);

  return sort;
};

export const ResponseTextList = ({ question, renderItem, items, rationaleMap }: ResponseTextListProps) => {

  const selectRespondentProps = useSelectRespondentProps();
  const selectStarred = useSelectStarredProjectSurveyAnswer(question.base.id);

  const values = useMemo(() => items
    .map(item => ({
      respondent: selectRespondentProps(item.userId),
      value: item.value,
    })),
  [selectRespondentProps, items]);

  return (
    <div>
      {
        values
          .map(item => {
            const userId = item.respondent.user.id;
            const starred = selectStarred(item.respondent.responseId);
            const rationale = rationaleMap[userId];
            return (
              <Fragment key={userId}>
                {renderItem({
                  rationale,
                  onToggleStar: starred.onToggle,
                  respondent: item.respondent,
                  starred: starred.value,
                  value: item.value,
                })}
              </Fragment>
            );
          })
      }
    </div>
  );
};

const pageSize = 50;

const ResponseText = (props: Props) => {

  const { query, setQueryState } = useProjectSurveyResponsesState<SurveyQuestionType.ShortTextResponse>();
  const page = useMemo(() => query.page || 1, [query.page]);

  const sortValues = useSortStarrableResponses(props.question.base.id);

  const items: ResponseItem[] = useMemo(() => {
    return Object.keys(props.responses).map(userId => {
      const data = props.responses[userId];
      const rationale = props.rationale[userId];
      return {
        rationale,
        userId,
        value: data.value,
      };
    });
  }, [
    props.rationale,
    props.responses,
  ]);

  const sortedItems = useMemo(() => {
    return sortValues(items);
  }, [
    items,
    sortValues,
  ]);

  const paginatedItems = useMemo(() => {
    return sortedItems
      .slice((page - 1) * pageSize, page * pageSize);
  }, [
    page,
    sortedItems,
  ]);

  const renderItem = useCallback((data: ResponseTextItemProps) => {
    return (
      <ResponseTextItem {...data} />
    );
  }, []);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryState({ page: pageIndex + 1 });
  }, [
    setQueryState,
  ]);

  const pageCount = useMemo(() => {
    return Math.ceil(items.length / pageSize);
  }, [items.length]);

  return (
    <div className={styles.root}>
      <ResponseTextList
        rationaleMap={props.rationale}
        items={paginatedItems}
        question={props.question}
        renderItem={renderItem} />
      {items.length > pageSize &&
        <div className={styles.footer}>
          <ControlledTablePagination
            goToPage={handlePageChange}
            pageIndex={page - 1}
            pageCount={pageCount}
            pageSize={pageSize}
            totalCount={items.length} />
        </div>
      }
    </div>
  );
};

export { ResponseText };
export default ResponseText;